// The rules of the photo chain, with no Cloudflare or network code in them, so
// they can be tested on their own (src/lib/__tests__/place-photos.test.ts).
// Everything that touches D1, R2 or Google lives in place-photos.server.ts.

export const PHOTO_NAME_RE = /^places\/[A-Za-z0-9_-]+\/photos\/[A-Za-z0-9_\-.]+$/;

// Cards ask for 400, the gallery for 800. Every extra width is a separate paid
// fetch and a separate R2 object, so nothing else is accepted.
export const PHOTO_WIDTHS = [400, 800] as const;

// Same ceiling the import used (tools/sync_places_v2.py, MAX_PHOTOS).
export const MAX_PHOTOS_PER_CLUB = 6;

// Photo fetches per calendar month, all callers together. Past this the site
// draws plates instead of calling Google.
//
// 900 on purpose: Google gives the first 1,000 Place Photos calls per month for
// free (pricing page read 21 Sep 2026) and bills USD 7 per 1,000 after that.
// Sam's rule is that photos may cost nothing, so the ceiling sits under the
// free volume with 100 to spare for the counter's small overshoot. Raising
// this number is a decision to start paying.
export const MONTHLY_FETCH_CEILING = 900;
// The share of that ceiling the refresh job may use for pre-warming. The rest
// stays free for visitors opening galleries, so a busy job can never be the
// reason a visitor sees a plate.
export const JOB_FETCH_CEILING = 600;
// Place Details calls per month. Not billed with the `photos` field mask, but
// an uncounted loop is how surprises start.
export const MONTHLY_DETAILS_CEILING = 2500;

// A club is due again after three weeks. The cached bytes are dropped at 30
// days, so this leaves nine days of slack: a week of failed runs still ends
// before any visitor meets a cold cache. A four-week cycle would leave two.
export const REFRESH_AFTER_DAYS = 21;
// Per invocation. The Workers Free plan allows 50 subrequests per invocation
// and counts every hop of a redirect. A club is one Details call plus two
// photo fetches, and Google answers each photo fetch with a redirect to the
// image host, so a club is five subrequests: eight clubs is 40, with room left
// for a retry. On the Paid plan (10,000) this could be raised, but the cron
// keeps up comfortably at eight.
export const MAX_CLUBS_PER_RUN = 8;
// After a self-heal attempt the proxy leaves that club alone for this long.
export const HEAL_COOLDOWN_HOURS = 6;
// The cache lifetime the project has always applied to Google imagery.
export const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export type GooglePhoto = {
  name?: string;
  widthPx?: number;
  heightPx?: number;
  authorAttributions?: { displayName?: string; uri?: string }[];
};

export type PickedPhoto = {
  name: string;
  attribution: string;
  attributionUri: string | null;
  widthPx: number | null;
  heightPx: number | null;
  sort: number;
};

// Same order logic as the import: Google's own order, first six, position is
// the sort key. Google puts the photo it considers most representative first,
// and sort 0 is what every card shows. Malformed names are skipped rather than
// stored, because the proxy would refuse them anyway.
export function pickPhotos(photos: GooglePhoto[] | undefined | null): PickedPhoto[] {
  const out: PickedPhoto[] = [];
  for (const p of photos ?? []) {
    if (out.length >= MAX_PHOTOS_PER_CLUB) break;
    if (!p?.name || !PHOTO_NAME_RE.test(p.name)) continue;
    const author = p.authorAttributions?.[0];
    out.push({
      name: p.name,
      attribution: author?.displayName ?? "",
      attributionUri: author?.uri ?? null,
      widthPx: p.widthPx ?? null,
      heightPx: p.heightPx ?? null,
      sort: out.length,
    });
  }
  return out;
}

export function placeIdFromPhotoName(name: string): string | null {
  const m = /^places\/([A-Za-z0-9_-]+)\/photos\//.exec(name);
  return m ? m[1] : null;
}

export function r2KeyFor(name: string, width: number): string {
  return `place-photo-cache/${width}/${name.replace(/\//g, "_")}.jpg`;
}

// How many of `want` units still fit under `ceiling`. Never negative, never
// more than asked: at the ceiling the answer is 0, one below it the answer is 1.
export function budgetRoom(spent: number, ceiling: number, want: number): number {
  if (!Number.isFinite(spent) || spent < 0) spent = 0;
  return Math.max(0, Math.min(Math.floor(want), Math.floor(ceiling - spent)));
}

export type RunPlan = {
  clubs: number;
  detailsCalls: number;
  photoCalls: number;
  stoppedBy: "nothing_due" | "per_run_limit" | "details_budget" | "photo_budget" | "all_due_done";
};

// What one run may do, given what is due and what the month has left. One club
// is one Details call plus, when pre-warming, one photo call per width. A club
// is only started if its whole cost fits, so a run never renews names it then
// cannot warm.
export function planRun(input: {
  due: number;
  perRunLimit: number;
  detailsSpent: number;
  fetchesSpent: number;
  warm: boolean;
}): RunPlan {
  const perClubPhotos = input.warm ? PHOTO_WIDTHS.length : 0;
  const limit = Math.max(0, Math.min(input.perRunLimit, MAX_CLUBS_PER_RUN));
  if (input.due <= 0) return { clubs: 0, detailsCalls: 0, photoCalls: 0, stoppedBy: "nothing_due" };

  const byDetails = budgetRoom(input.detailsSpent, MONTHLY_DETAILS_CEILING, input.due);
  const byPhotos = input.warm
    ? Math.floor(
        budgetRoom(input.fetchesSpent, JOB_FETCH_CEILING, input.due * perClubPhotos) /
          perClubPhotos,
      )
    : input.due;
  const clubs = Math.min(input.due, limit, byDetails, byPhotos);

  // The normal reason a run stops short is its own size limit; a budget is only
  // named when it, and not the limit, was the smallest number.
  let stoppedBy: RunPlan["stoppedBy"] = "all_due_done";
  if (clubs < input.due) {
    if (clubs === limit) stoppedBy = "per_run_limit";
    else if (clubs === byDetails) stoppedBy = "details_budget";
    else stoppedBy = "photo_budget";
  }
  return { clubs, detailsCalls: clubs, photoCalls: clubs * perClubPhotos, stoppedBy };
}

export type PhotoFailure = "expired_name" | "key_or_billing" | "quota" | "upstream";

// Why Google said no. The distinction matters because only one of these is
// something the app can fix by itself (a fresh name); the others need Sam in
// the Google Cloud console, and the log line should say so.
//
// An invalid API key comes back as 400 INVALID_ARGUMENT, the same status as an
// expired photo name, so the body is checked before the status.
export function classifyGoogleFailure(status: number, body: string): PhotoFailure {
  const b = body.toLowerCase();
  if (
    b.includes("api_key") ||
    b.includes("api key") ||
    b.includes("billing") ||
    b.includes("permission_denied") ||
    b.includes("service_disabled") ||
    status === 401 ||
    status === 403
  ) {
    return "key_or_billing";
  }
  if (status === 429 || b.includes("resource_exhausted") || b.includes("quota")) return "quota";
  if (status === 400 || status === 404) return "expired_name";
  return "upstream";
}

// Own photos are served from /api/photo/<key>. The 400px variant is requested
// with ?w=400; the route falls back to the original when no variant exists, so
// asking is always safe. Anything else (an absolute URL, a static asset) is
// left exactly as it is.
export function ownPhotoUrl(image: string, width: 400 | 800): string {
  if (!image.startsWith("/api/photo/") || image.includes("?")) return image;
  return width === 400 ? `${image}?w=400` : image;
}

export type CoverSource = { src: string; kind: "own" | "licensed" | "google" };

// Own photos live under club-photos/<slug>/... in R2. The ones
// tools/fetch_licensed_photos.py stores sit one level deeper, under
// club-photos/<slug>/licensed/, and that segment is the whole signal: such a
// photo shows the beach NEAR the club, found on Wikimedia Commons or
// Openverse, not the club itself, and the page must say so. A separate column
// would cost a row read on every listing query; the key is already selected.
export function imageKind(image: string): "own" | "licensed" {
  return /^\/api\/photo\/[^/?]+\/licensed\//.test(image) ? "licensed" : "own";
}

// The order, in one place: the club's own photo (uploaded or supplied by the
// owner), then a freely licensed photo of its surroundings, then Google's, then
// nothing, and "nothing" is what makes the caller draw the plate. `image` is
// one column, so own and licensed cannot both be the cover: the SQL that sets
// a licensed cover refuses to overwrite an own one (see the tool), which is
// how the order own > licensed holds for the cover. In the gallery both can
// appear, own first (clubGallery sorts on source).
//
// All candidates are returned, in order, so that a photo which fails to load
// can fall through to the next one instead of leaving a broken image.
export function coverSources(
  club: { image: string | null; google_photo: string | null },
  width: 400 | 800 = 400,
): CoverSource[] {
  const out: CoverSource[] = [];
  if (club.image) out.push({ src: ownPhotoUrl(club.image, width), kind: imageKind(club.image) });
  if (club.google_photo && PHOTO_NAME_RE.test(club.google_photo)) {
    out.push({ src: `/api/place-photo/${club.google_photo}?w=${width}`, kind: "google" });
  }
  return out;
}

// "Photo: Jane Doe / CC BY-SA 4.0". Only built when the licence asks for it.
export function creditLine(p: {
  credit_required: number | boolean | null;
  credit_name: string | null;
  license_name: string | null;
}): string | null {
  if (!p.credit_required || !p.credit_name) return null;
  return p.license_name ? `${p.credit_name} / ${p.license_name}` : p.credit_name;
}
